import { useState } from 'react';
import SeoTitle from '@/components/SeoTitle';
import { HomePageJsonLd } from '../../components/SeoJsonLd';
import Navbar from '@/components/feature/Navbar';
import RoiCtaStrip from '@/components/feature/RoiCtaStrip';
import Footer from '@/components/feature/Footer';
import EncyclopediaSection from './components/EncyclopediaSection';
import ProcessSection from './components/ProcessSection';
import PopularMaterials from './components/PopularMaterials';
import StudyEncyclopediaSection from './components/StudyEncyclopediaSection';
import PopularProgramsSection from './components/PopularProgramsSection';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    comments: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    const phoneRegex = /^\+?\d{10,15}$/;
    return phoneRegex.test(cleanPhone);
  };

  const normalizePhone = (phone: string): string => {
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    if (!cleanPhone.startsWith('+')) {
      cleanPhone = '+' + cleanPhone;
    }
    return cleanPhone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setErrorMessage('Пожалуйста, укажите ваше имя');
      return;
    }
    if (!formData.phone.trim()) {
      setErrorMessage('Пожалуйста, укажите ваш телефон');
      return;
    }
    if (!privacyAccepted) {
      setErrorMessage('Пожалуйста, подтвердите согласие на обработку персональных данных');
      return;
    }
    
    if (!validatePhone(formData.phone)) {
      setErrorMessage('Пожалуйста, введите корректный номер телефона (минимум 10 цифр)');
      return;
    }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const normalizedPhone = normalizePhone(formData.phone);
      
      const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
      const params = new URLSearchParams({
        'fields[NAME]': formData.name,
        'fields[TITLE]': `Лид с businesstraining ${formData.name}`,
        'fields[COMPANY_TITLE]': formData.company,
        'fields[PHONE][0][VALUE]': normalizedPhone,
        'fields[COMMENTS]': formData.comments,
        'params[REGISTER_SONET_EVENT]': 'Y',
        'ASSIGNED_BY_ID': '1'
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`, {
        method: 'GET'
      });

      if (response.ok) {
        const result = await response.json();
        if (result.result) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', company: '', comments: '' });
          
          setTimeout(() => {
            setFormStatus('idle');
          }, 5000);
        } else {
          throw new Error('Ошибка отправки формы');
        }
      } else {
        throw new Error('Ошибка отправки формы');
      }
    } catch (error) {
      setFormStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
      
      setTimeout(() => {
        setFormStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  const homeNavLinks = [
    { label: 'Услуги', target: 'services' },
    { label: 'Преимущества', target: 'advantages' },
    { label: 'Тренер', target: 'trainer' },
    { label: 'FAQ', target: 'faq' },
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SeoTitle title="Корпоративные тренинги в Минске | Business Training" />
      <HomePageJsonLd />
      
      <Navbar
        links={homeNavLinks}
        ctaLabel="Заказать тренинг"
        ctaTarget="contact"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute top-1/2 left-1/2 w-[177.77777778vh] min-w-full h-[56.25vw] min-h-full -translate-x-1/2 -translate-y-1/2 object-cover"
          >
            <source src="https://business-training.by/Load/vid1.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/75"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full pt-32 md:pt-28 lg:pt-20">
          <h1 className="text-3xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight">
            Корпоративные тренинги<br />и обучение сотрудников<br />в Минске и Беларуси
          </h1>
          <p className="text-base md:text-2xl text-white/95 mb-8 md:mb-10 max-w-4xl mx-auto leading-relaxed">
            Проводим <strong>корпоративные тренинги</strong> для компаний, <strong>обучение сотрудников</strong>, тимбилдинги и бизнес-игры, которые помогают развивать продажи, переговорные навыки, лидерство и soft skills. Наши программы адаптированы для команд любых размеров и отраслей.
          </p>
          <a href="#contact" className="inline-block bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer mb-16 md:mb-0">
            Оставить заявку
          </a>
        </div>
      </section>

      {/* Для кого */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Для кого наши корпоративные тренинги
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Наши программы разработаны для разных категорий специалистов и адаптируются под специфику вашего бизнеса
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Руководители и управленцы</h3>
              <p className="text-gray-600 leading-relaxed">
                <strong>Обучение лидерству</strong>, развитию управленческих навыков, стратегическому мышлению и принятию решений
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Отделы продаж</h3>
              <p className="text-gray-600 leading-relaxed">
                <strong>Тренинги по продажам</strong>, работе с возражениями, коммуникациям с клиентами, увеличение конверсии и средний чек
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Команды любых отделов</h3>
              <p className="text-gray-600 leading-relaxed">
                <strong>Тимбилдинги</strong>, командные бизнес-игры, развитие soft skills и коммуникаций, разрешение конфликтов
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Компании</h3>
              <p className="text-gray-600 leading-relaxed">
                Которые хотят <strong>удерживать сотрудников</strong> и повышать эффективность бизнес-процессов, развивать корпоративную культуру
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Услуги */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Наши услуги
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Комплексные программы обучения для развития ключевых компетенций вашей команды
          </p>
          
          {/* Корпоративные тренинги */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Корпоративные тренинги</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-presentation-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Обучение продажам</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Повышение конверсии, работа с возражениями, сценарии встреч. <strong>Тренинги по продажам</strong> для увеличения эффективности отдела продаж и построение долгосрочных отношений с заказчиками
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-discuss-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Тренинги по переговорам</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Эффективные стратегии, метасообщения, психологические приемы. <strong>Тренинг по переговорам для менеджеров</strong> и достижение win-win решений
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-shield-star-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Тренинги по лидерству</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Развитие управленческих компетенций, управление командой и мотивация. <strong>Обучение управленцев Беларусь</strong> для создания эффективной рабочей среды
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Тимбилдинг и бизнес-игры */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Тимбилдинг и бизнес-игры</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-gamepad-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Бизнес-игры для команд</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Развивают стратегическое мышление, командное взаимодействие и коммуникации. <strong>Бизнес-игра для команды</strong> — интерактивное обучение с практическим применением навыков
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-group-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Корпоративные тимбилдинги</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Формируют сплочённую команду, улучшают атмосферу в компании. <strong>Корпоративный тимбилдинг Беларусь</strong> для укрепления связей в коллективе и развития доверия
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Soft skills и коммуникации */}
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Soft Skills и коммуникации</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-chat-smile-3-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Обучение коммуникациям</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Эффективное общение внутри команды и с клиентами. <strong>Тренинги по коммуникациям Минск</strong> для улучшения взаимодействия между сотрудниками
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Развитие Soft Skills</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Эмоциональный интеллект, стресс-менеджмент, самопрезентация. <strong>Обучение soft skills для сотрудников</strong>, тайм-менеджмент и адаптивность к изменениям
                </p>
                <a href="#contact" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Заказать тренинг <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Энциклопедия корпоративного обучения */}
      <EncyclopediaSection />

      <RoiCtaStrip />

      {/* Изучите корпоративное обучение */}
      <StudyEncyclopediaSection />

      {/* Популярные программы обучения */}
      <PopularProgramsSection />

      {/* Как выбрать программу обучения */}
      <ProcessSection />

      {/* Преимущества */}
      <section id="advantages" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-16">
            Преимущества наших тренингов
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Практическая направленность</h3>
              <p className="text-gray-600 leading-relaxed">
                Сразу применимо на работе. <strong>Корпоративный тренинг с практикой Минск</strong> — минимум теории, максимум реальных кейсов
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-settings-line text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Индивидуальный подход</h3>
              <p className="text-gray-600 leading-relaxed">
                К каждому отделу и компании. Программа адаптируется под специфику вашего бизнеса и текущие задачи
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-medal-line text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Опытные тренеры</h3>
              <p className="text-gray-600 leading-relaxed">
                С 18+ годами практики в продажах и управлении. Эксперты с реальным опытом в бизнесе
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-global-line text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Онлайн и офлайн</h3>
              <p className="text-gray-600 leading-relaxed">
                Возможность проведения как оффлайн, так и онлайн. Совмещение обучения и развлечения — бизнес-игры и интерактивные упражнения
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Кто проводит обучение */}
      <section id="trainer" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-16">
            Кто проводит обучение
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/d5f5f47c7d94e3dc67c5ef073553230f.jpeg" 
                  alt="Алексей Сивко - бизнес-тренер по корпоративным тренингам Минск"
                  title="Алексей Сивко - эксперт по корпоративным тренингам"
                  className="w-full h-full object-cover md:object-[center_20%] object-top"
                />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Алексей Сивко</h3>
              <p className="text-xl text-teal-600 font-semibold mb-6">
                <a href="https://sivko.by/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-700 transition-colors">Бизнес-тренер</a> с более чем 18-летним опытом в продажах и переговорах
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700 leading-relaxed">
                    Провёл <strong>сотни тренингов</strong> для руководителей и команд в Беларуси, России и Казахстане
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Эксперт по корпоративным продажам</strong>, переговорам, soft skills и тимбилдингу
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700 leading-relaxed">
                    Автор концепции <strong>«Культурология продаж»</strong>, которая помогает компаниям строить долгосрочные отношения с клиентами
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Сертифицированный бизнес-тренер</strong> с международными квалификациями и уникальными методиками
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-teal-600 mb-8">
                <p className="text-gray-700 italic leading-relaxed">
                  «Моя цель — не просто передать знания, а помочь каждому участнику раскрыть свой потенциал и достичь конкретных результатов в бизнесе. Я верю, что эффективное обучение — это практика, обратная связь и постоянное развитие.»
                </p>
              </div>
              <Link
                to="/o-trenere"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                О тренере
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Популярные материалы */}
      <PopularMaterials />

      {/* FAQ */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-16">
            Часто задаваемые вопросы
          </h2>
          <div className="space-y-4">
            {[
              {
                question: 'Какие тренинги по продажам проводятся для отдела продаж?',
                answer: 'Мы проводим корпоративные тренинги по продажам в Минске и Беларуси, которые помогают увеличить конверсию, улучшить работу с возражениями и повысить средний чек. Программы включают практические кейсы, ролевые игры и сценарии встреч, адаптированные под специфику вашего бизнеса и уровень сотрудников. Наши тренинги по продажам также доступны для компаний в России и Казахстане.'
              },
              {
                question: 'Можно ли заказать корпоративный тренинг онлайн в Минске и других регионах?',
                answer: 'Да, наши тренинги проводятся как офлайн в Минске и Беларуси, так и онлайн для удалённых команд в России, Казахстане и других странах. Онлайн-формат включает интерактивные задания, бизнес-игры и практические упражнения, что позволяет достичь таких же результатов, как при очном обучении.'
              },
              {
                question: 'Что такое корпоративный тренинг и чем он полезен?',
                answer: 'Корпоративный тренинг — это обучение сотрудников, направленное на развитие ключевых навыков, повышение эффективности работы и улучшение командной коммуникации. Наши тренинги помогают руководителям, отделам продаж и командам любых направлений в Минске, Беларуси, России и Казахстане достигать конкретных бизнес-результатов.'
              },
              {
                question: 'Какие тренинги по переговорам вы проводите?',
                answer: 'Мы обучаем менеджеров эффективным стратегиям переговоров, работе с метасообщениями, психологическим приёмам и техникам достижения win-win решений. Программа адаптируется под специфику вашей отрасли и уровень сотрудников. Наши тренинги по переговорам проводятся для компаний в Минске, Беларуси, России и Казахстане.'
              },
              {
                question: 'Что такое бизнес-игра и тимбилдинг?',
                answer: 'Бизнес-игра — это интерактивное командное обучение, развивающее стратегическое мышление, навыки совместной работы и коммуникации. Тимбилдинг формирует сплочённость команды, улучшает атмосферу в коллективе и помогает повысить мотивацию сотрудников. Мы проводим бизнес-игры и тимбилдинги для команд в Минске, Беларуси, России и Казахстане.'
              },
              {
                question: 'Можно ли провести тимбилдинг для всей команды компании?',
                answer: 'Да, мы организуем корпоративные тимбилдинги и бизнес-игры для команд любого размера. Это интерактивное обучение, которое развивает стратегическое мышление, коммуникации и сплочённость коллектива. Возможна организация как офлайн, так и онлайн.'
              },
              {
                question: 'Сколько стоят корпоративные тренинги?',
                answer: 'Стоимость тренингов зависит от формата, продолжительности и выбранной программы. Мы подбираем индивидуальные решения для каждой компании, чтобы обучение было максимально эффективным и рентабельным. Цены на корпоративные тренинги в Минске, Беларуси, России и Казахстане обсуждаются индивидуально.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <i className="ri-arrow-up-s-line text-2xl text-teal-600 flex-shrink-0"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-2xl text-teal-600 flex-shrink-0"></i>
                  )}
                </button>
                <div className={`px-8 pb-6 ${openFaq === index ? '' : 'hidden'}`}>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-10 md:p-16 shadow-2xl">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
                Закажите корпоративный тренинг прямо сейчас
              </h2>
              <p className="text-base md:text-xl text-white/95 max-w-3xl mx-auto leading-relaxed">
                Увеличьте эффективность команды и продажи с помощью <strong>корпоративных тренингов</strong> от эксперта с более чем 18-летним опытом!
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              <form 
                id="contact_form" 
                data-readdy-form 
                onSubmit={handleSubmit}
                className="bg-white rounded-xl p-5 sm:p-8 shadow-lg"
              >
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Имя <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                      placeholder="Введите ваше имя"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Телефон <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                      placeholder="+375 (XX) XXX-XX-XX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                      Название компании
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                      placeholder="Введите название компании"
                    />
                  </div>

                  <div>
                    <label htmlFor="comments" className="block text-sm font-semibold text-gray-900 mb-2">
                      Запрос
                    </label>
                    <textarea
                      id="comments"
                      name="comments"
                      value={formData.comments}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base resize-none"
                      placeholder="Опишите ваш запрос или вопрос (до 500 символов)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.comments.length}/500 символов
                    </p>
                  </div>

                  {errorMessage && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {errorMessage}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      ✓ Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.
                    </div>
                  )}

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">
                      Я согласен(на) на <a href="https://sivko.by/load/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline">обработку персональных данных</a>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || !privacyAccepted}
                    className="w-full bg-teal-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? 'Отправка...' : 'Отправить заявку'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Корпоративные тренинги в Минске для развития бизнеса
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Наши <strong>корпоративные тренинги в Минске</strong> помогают компаниям в Беларуси, России и Казахстане развивать ключевые компетенции сотрудников. Мы специализируемся на <strong>тренингах по продажам</strong>, переговорам, лидерству и командообразовании.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Обучение сотрудников</strong> проводится опытным бизнес-тренером с 20-летним стажем. Программы адаптируются под специфику вашего бизнеса и включают практические кейсы, ролевые игры и интерактивные упражнения для максимальной эффективности.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Корпоративные тренинги для отдела продаж Минск</strong> — это инвестиция в рост вашего бизнеса. Мы помогаем компаниям увеличивать конверсию, улучшать коммуникации с клиентами и развивать навыки переговоров.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Бизнес-игры и тимбилдинг для команды
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Бизнес-игра для команды</strong> — это эффективный инструмент развития стратегического мышления и навыков совместной работы. <strong>Бизнес-игра для руководителей</strong> помогает развивать управленческие компетенции в игровом формате.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Наши <strong>корпоративные тимбилдинги</strong> укрепляют командный дух и улучшают коммуникацию между сотрудниками. <strong>Бизнес-игра для отдела продаж</strong> развивает навыки работы с клиентами и повышает мотивацию команды.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Мы также проводим <strong>обучение soft skills для сотрудников</strong>, включая эмоциональный интеллект, тайм-менеджмент и адаптивность. <strong>Заказать корпоративный тренинг Минск</strong> можно в онлайн и офлайн форматах для максимального удобства вашей команды.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}